import {autoImplement} from '@/util';
import moment, {Moment} from 'moment';
import Dates, {ApiDates} from './Dates';
import {ApiOrderPosition} from './OrderPosition';
import {ApiPosition} from './Position';
import Supplier, {ApiSupplier} from './Supplier';
import {FulltextSearch} from '@/api/meta/filters';

export interface ApiOrder {
  Lieferant: ApiSupplier;
  Termine?: ApiDates;
  Nummer: number;
  Datum: string;
  Uhrzeit: string;
  Bediener: string;
  Erledigt: 'J' | 'N';
  Niederlassung: number;
  Kostenstelle: number;
  Kommission: number|null;
  KundeNrL3: number|null;
  UmrFaktor: number|null;
  Text1: string|null;
  Text2: string|null;
  Skonto1Tg: number|null;
  Skonto1Pr: number|null;
  Skonto2Tg: number|null;
  Skonto2Pr: number|null;
  NettoTg: number|null;
  Paritaet: any|null;
  ObjektNummer: any|null;
  Fremdwaehrung: string|'';
  AnzahlLieferungen?: number;
  BestellungPosition?: ApiOrderPosition[];
}

export interface OrderFilters {
  'Lieferant/LieferantName$Contains'?: string;
  'Lieferant/LieferantNr'?: number;
  Nummer?: number;
  'Termine/GeplantLieferDatum$From'?: string;
  'Termine/GeplantLieferDatum$To'?: string;
  Erledigt?: 'J' | 'N';
  Fulltext$Fulltext: FulltextSearch;
}

export interface CrmOrderFilters extends OrderFilters {
  'Fulltext$Fulltext': FulltextSearch;
}

export interface DetailOrderFilters {
  Nummer?: number;
}

interface OrderInterface extends ApiOrder {
  Lieferant: Supplier;
  Termine: Dates;
  DatumParsed: Moment;
  UhrzeitParsed: Moment;
  ErledigtBool: boolean;
}

export default class Order extends autoImplement<OrderInterface>() {
  static fromApi(apiOrder: ApiOrder): Order {
    return new Order({
      ...apiOrder,
      Lieferant: Supplier.fromApi(apiOrder.Lieferant),
      Termine: Dates.fromApi(apiOrder.Termine ?? {GeplantLieferDatum: null, GeplantLieferUhrzeit: null}),
      DatumParsed: moment(apiOrder.Datum),
      UhrzeitParsed: moment(apiOrder.Datum.replace('00:00', apiOrder.Uhrzeit)),
      ErledigtBool: apiOrder.Erledigt === 'J',
    });
  }

  get time() {
    return this.UhrzeitParsed.format('HH:mm');
  }

  get date() {
    return this.DatumParsed.format('DD.MM.YYYY');
  }

  get convertedPositions(): ApiPosition[] {
    const originalPositions = this.BestellungPosition ?? [];
    return originalPositions.map((position) => {
      return {
        Posnr: position.PosNr,
        Artikelnr: position.Artikel.ArtikelNr,
        ArtikelArt: position.Artikel.ArtikelArt,
        Bezeichn1: position.Artikel.ArtikelBezeichnung1,
        Bezeichn2: position.Artikel.ArtikelBezeichnung2,

        Menge: position.Einheit.Menge,
        Einheit: position.Einheit.Einheit,
        Eancode: position.Artikel.EAN,

        Gebindemenge: position.Gebinde.GebindeMenge,
        Gebindeinh: position.Gebinde.GebindeEinheit,
        Gebinhalt: position.Gebinde.GebindeMenge ? position.Einheit.Menge / position.Gebinde.GebindeMenge : null,
        GebindeSchluessel: position.Gebinde.GebindeSchluesselL3 ?? 0,
        // eslint-disable-next-line camelcase
        EAN_Gebinde: position.EAN_Gebinde,

        Palettenmenge: position.Paletten.PalettenAnzahl,
        Palettennr: position.Paletten.PalettenNrL3,
        Umrpalgebinde: position.Paletten.PalettenAnzahlGebinde,
        Umrpalmenge: position.Paletten.PalettenInhaltGebinde,

        Chargenpflicht: position.Artikel.Chargenpflicht,
        Seriennrpflicht: position.Artikel.Seriennrpflicht,

        Matchcode: '',
        GruppenBezeichnung: '',

        // Auftrag
        Belegnr: this.Nummer,
        Datum: this.Datum,
        Name: this.Lieferant.LieferantName,

        Buchungen: [],
        VerfuegbareNVEs: [],
        Chargen: [],
        Restmenge: position.Einheit.Menge,

        Bediener: this.Bediener,

        ArtikelEAN: structuredClone(position.ArtikelEAN),
      };
    });
  }


  get color() {
    return this.ErledigtBool ? 'error' : (this.Termine.GeplantLieferDatumParsed?.isAfter() ? 'primary' : 'success');
  }

  get statusText() {
    return this.ErledigtBool ? 'erledigt' : (this.Termine.GeplantLieferDatumParsed?.isAfter() ? 'offen' : 'verpasst');
  }

  get icon() {
    return this.ErledigtBool ? 'mdi-check' : (this.Termine.GeplantLieferDatumParsed?.isAfter() ? null : 'mdi-clock-alert');
  }
  get DatumFormatted() {
    return moment(this.Datum).format('DD.MM.YYYY');
  }
  get GeplantesLieferDatumFormatted() {
    return this.Termine.GeplantLieferDatum ? moment(this.Termine.GeplantLieferDatumParsed).format('DD.MM.YYYY') : null;
  }
}
